// JewelryInfoForm.js
import React, { useEffect, useState } from "react";
import { Form, Row, Col, Modal, Button } from "react-bootstrap";
import { isObject, renderValue } from "../../utils/jewelleryHelpers";
import JewelryTable from "./JewelleryTable";
import jewellery from "lib/jewellery-client";
import "./JewelryInfoForm.css";
import LoadingModel from "components/Event/LoadingModel";

const VALIDATION_FIELD_MAP = {
  cut: "CutCode",
  shape: "ShapeCode",
  rm: "RawMaterialCode",
  mm_size: "MMSizeCode",
  commodity: "CommodityName",
  color: "ColorCode",
  sieve_size: "SieveSizeCode",
  setting_type: "SettingTypeName",
};

const EXCLUDED_VALIDATION_FIELDS = ["pp_wt", "pieces", "wt"];

const JewelryInfoForm = ({ data = {}, show, onHide, type }) => {
  const [validatedData, setValidatedData] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [tableData, setTableData] = useState(data);
  const [selectedValues, setSelectedValues] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [hasValidated, setHasValidated] = useState(false);
  const [isFetchingWeights, setIsFetchingWeights] = useState(false);
 const [isLoading, setIsLoading] = useState(false);

  const tableKey = type === "design" ? "stone_details" : "diamond_info";

  useEffect(() => {
    if (show) {
      setTableData(data);
      setSelectedValues({});
      setValidatedData({});
      setHasValidated(false);
    }
  }, [show, data]);

  const handleValidate = async () => {
  setIsValidating(true);
    setIsLoading(true);
  try {
    const rows = Array.from(
      (tableData[tableKey] || []).reduce((acc, obj) => {
        Object.keys(obj).forEach((key) => acc.add(key));
        return acc;
      }, new Set())
    );

    const { data: results, error } = await jewellery.designExtractValidate({
      rows,
      type,
    });

    if (error) {
      console.error("Validation error:", error);
      return;
    }

    const transformed = {};
    for (const [key, val] of Object.entries(results || {})) {
      const lowerKey = key.toLowerCase();
      transformed[lowerKey] = Array.isArray(val) ? val : val?.valid || [];
    }

    setValidatedData(transformed);
    setHasValidated(true);

    // Only call fetchWeights if we need to get pp_wt values
    const needsPpWt = (tableData[tableKey] || []).some(item => 
      !item.pp_wt || item.pp_wt === 'NA'
    );
    
    if (needsPpWt) {
      await handleFetchWeights();
    } else {
      // Calculate weights with existing pp_wt values
      setTableData(prev => {
        const newTableData = { ...prev };
        newTableData[tableKey] = (newTableData[tableKey] || []).map(item => {
          const pieces = Number(item.pieces) || 0;
          const pp_wt = item.pp_wt === 'NA' ? null : Number(item.pp_wt) || 0;
          
          const min_wt = pp_wt !== null && pieces ? (pp_wt * pieces).toFixed(3) : 'NA';
          const max_wt = pp_wt !== null && pieces ? (pp_wt * pieces).toFixed(3) : 'NA';
          
          return {
            ...item,
            min_wt,
            max_wt
          };
        });
        return newTableData;
      });
    }
  } catch (err) {
    console.error("Validation failed:", err);
  } finally {
    setIsValidating(false);
     setIsLoading(false);
  }
};

  const handleFetchWeights = async () => {
  setIsFetchingWeights(true);
   setIsLoading(true);
  try {
    const { data: updatedData, error } = await jewellery.fetchWeights({
      tableData,
      tableKey,
    });

    if (error) throw new Error(error);

    setTableData(prev => {
      const newTableData = { ...prev };
      newTableData[tableKey] = updatedData.map(item => {
        const pieces = Number(item.pieces) || 0;
        const pp_wt = item.pp_wt === 'NA' ? null : Number(item.pp_wt) || 0;
        
        // Condition 1: If pp_wt exists, calculate weights
        // Condition 2: If pp_wt is NA, set weights to NA
        const min_wt = pp_wt !== null && pieces ? (pp_wt * pieces).toFixed(3) : 'NA';
        const max_wt = pp_wt !== null && pieces ? (pp_wt * pieces).toFixed(3) : 'NA';
        
        return {
          ...item,
          pp_wt: item.pp_wt, // Keep original value (could be 'NA' or number)
          min_wt,
          max_wt
        };
      });
      return newTableData;
    });
  } catch (error) {
    console.error("Error:", error);
  } finally {
    setIsFetchingWeights(false);
    setIsLoading(false); 
  }
};

const handleSelectChange = async (tableKey, rowIndex, field, value) => {
  const lowerField = field.toLowerCase();
  const key = `${tableKey}_${rowIndex}_${field}`;
  
  // Update selected values
  setSelectedValues(prev => ({
    ...prev,
    [key]: value,
  }));

  // Update table data immediately for the changed field
  setTableData(prev => {
    const newData = { ...prev };
    const rows = [...newData[tableKey]];
    rows[rowIndex] = { ...rows[rowIndex], [field]: value };
    newData[tableKey] = rows;
    return newData;
  });

  // If changing sieve size, fetch new pp_wt from API
  if (lowerField === 'sieve_size' && hasValidated) {
    try {
      setIsLoading(true);;
      const { data: updatedData, error } = await jewellery.fetchWeights({
        tableData: {
          ...tableData,
          [tableKey]: tableData[tableKey].map((item, idx) => 
            idx === rowIndex ? { ...item, [field]: value } : item
          )
        },
        tableKey,
      });

      if (error) throw new Error(error);

      setTableData(prev => {
        const newData = { ...prev };
        const rows = [...newData[tableKey]];
        const updatedRow = updatedData[rowIndex];
        
        const newPpWt = updatedRow?.pp_wt === 'NA' ? null : Number(updatedRow?.pp_wt) || 0;
        const pieces = Number(rows[rowIndex]?.pieces) || 0;
        
        rows[rowIndex] = { 
          ...rows[rowIndex], 
          pp_wt: updatedRow?.pp_wt || null,
          min_wt: newPpWt !== null && pieces ? (newPpWt * pieces).toFixed(3) : 'NA',
          max_wt: newPpWt !== null && pieces ? (newPpWt * pieces).toFixed(3) : 'NA'
        };
        
        newData[tableKey] = rows;
        return newData;
      });

      // Update selectedValues for pp_wt
      const ppWtKey = `${tableKey}_${rowIndex}_pp_wt`;
      setSelectedValues(prev => ({
        ...prev,
        [ppWtKey]: updatedData[rowIndex]?.pp_wt || null,
      }));
    } catch (error) {
      console.error("Error fetching weights:", error);
    } finally {
       setIsLoading(false); 
    }
  }

  // If changing pieces or pp_wt, calculate weights immediately
  if (lowerField === 'pieces' || lowerField === 'pp_wt') {
    setTableData(prev => {
      const newData = { ...prev };
      const rows = [...newData[tableKey]];
      const row = { ...rows[rowIndex] };
      
      const pieces = lowerField === 'pieces' ? Number(value) || 0 : Number(row.pieces) || 0;
      const pp_wt = lowerField === 'pp_wt' ? 
        (value === 'NA' ? null : Number(value) || 0) : 
        (row.pp_wt === 'NA' ? null : Number(row.pp_wt) || 0);
      
      rows[rowIndex] = {
        ...row,
        [field]: value,
        min_wt: pp_wt !== null && pieces ? (pp_wt * pieces).toFixed(3) : 'NA',
        max_wt: pp_wt !== null && pieces ? (pp_wt * pieces).toFixed(3) : 'NA'
      };
      
      newData[tableKey] = rows;
      return newData;
    });
  }
};

   const isValidField = (field, value) => {
    const lowerField = field.toLowerCase();
    const options = validatedData[lowerField] || [];
    const fieldKey = VALIDATION_FIELD_MAP[lowerField] || "code";

    return options.some((opt) => 
      String(opt[fieldKey]).toLowerCase() === String(value).toLowerCase()
    );
  };

  const handleSubmit = () => {
    const originalRows = tableData[tableKey] || [];
    let hasInvalid = false;

    const updatedRows = originalRows.map((row, rowIndex) => {
      const newRow = { ...row };

      for (const field of Object.keys(row)) {
        const key = `${tableKey}_${rowIndex}_${field}`;
        if (selectedValues.hasOwnProperty(key)) {
          newRow[field] = selectedValues[key];
        }

        const lowerField = field.toLowerCase();
        if (
          !EXCLUDED_VALIDATION_FIELDS.includes(lowerField) &&
          validatedData[lowerField]?.length > 0
        ) {
          const isValid = isValidField(field, newRow[field]);
          if (!isValid) {
            hasInvalid = true;
            console.warn("Invalid field detected:", {
              rowIndex,
              field,
              value: newRow[field],
              expected: validatedData[lowerField].map(
                (o) => o[VALIDATION_FIELD_MAP[lowerField] || "code"]
              ),
            });
          }
        }
      }

      return newRow;
    });

    if (hasInvalid) {
      setErrorMessage(
        "Some fields contain invalid values. Please correct them before saving."
      );
      return;
    }

    setTableData({ ...data, [tableKey]: updatedRows });
    setSelectedValues({});
    setErrorMessage("");
    console.log("✅ Ready to submit:", { ...data, [tableKey]: updatedRows });
  };

  return (
    <>
    <LoadingModel show={isLoading} />
      <Modal
        show={show}
        onHide={onHide}
        fullscreen
        scrollable
        centered
        backdrop="static"
      >
        <Modal.Header className="modal-header-custom">
          <Modal.Title>Jewelry Design Information</Modal.Title>
          <Button variant="outline-secondary" onClick={onHide}>
            X
          </Button>
        </Modal.Header>

        <Modal.Body style={{ padding: "0.5rem 1rem" }}>
          <Form>
            <Row>
              {Object.entries(tableData).map(([key, value]) => {
                if (
                  Array.isArray(value) &&
                  value.length &&
                  isObject(value[0])
                ) {
                  return (
                    <Col md={12} key={key} className="mb-1">
                      <h5>{key.replace(/_/g, " ")}</h5>
                      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                        <JewelryTable
                          data={value}
                          tableKey={key}
                          validatedData={validatedData}
                          onSelectChange={(rowIndex, field, val) =>
                            handleSelectChange(key, rowIndex, field, val)
                          }
                          selectedValues={selectedValues}
                          isValidating={isValidating || isFetchingWeights}
                          hasValidated={hasValidated}
                        />
                      </div>
                    </Col>
                  );
                }
                return renderFormField(key, renderValue(value));
              })}
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="btn btn-sm"
            onClick={handleValidate}
            disabled={isValidating || isFetchingWeights}
          >
            {isValidating
              ? "Validating..."
              : isFetchingWeights
              ? "Fetching Weights..."
              : "Validate"}
          </Button>
          <Button className="btn btn-sm" variant="secondary" onClick={onHide}>
            Close
          </Button>
          <Button
            className="btn btn-sm"
            variant="success"
            onClick={handleSubmit}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!errorMessage}
        onHide={() => setErrorMessage("")}
        centered
        size="sm"
        backdrop="static"
      >
        <Modal.Header className="bg-danger text-white py-2">
          <Modal.Title>Validation Error</Modal.Title>
          <Button
            variant="outline-secondary"
            onClick={() => setErrorMessage("")}
          >
            X
          </Button>
        </Modal.Header>
        <Modal.Body className="text-center custom-error-body">
          {errorMessage}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="danger" onClick={() => setErrorMessage("")}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const renderFormField = (key, value) => (
  <Col md={2} key={key} className="mb-2">
    <Form.Group>
      <Form.Label style={{ fontSize: "0.75rem" }}>
        {key.replace(/_/g, " ")}
      </Form.Label>
      <Form.Control
        size="sm"
        type="text"
        value={value}
        readOnly
        className="text-truncate"
        style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
      />
    </Form.Group>
  </Col>
);

export default React.memo(JewelryInfoForm);